import React, { useState, useCallback } from 'react';
import { format, eachDayOfInterval, startOfMonth, endOfMonth, isSameDay, parseISO, setMonth, getDay, isToday, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { Room, Booking } from './BookingCalendar';
import { EditBookingDialog } from './EditBookingDialog';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDevice } from '@/hooks/use-device';

type MonthlyCalendarProps = {
  rooms: Room[];
  bookings: Booking[];
  selectedRoom: Room | null;
  date: Date;
  onDayClick?: (date: Date) => void;
  onMonthChange: (monthIndex: number) => void;
  onBookingUpdate: (booking: Booking) => void;
  onBookingDelete: (bookingId: number) => void;
};

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Fixed version with unique abbreviations
const shortWeekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const statusColors = {
  paid: 'bg-blue-400 shadow hover:shadow-md hover:bg-blue-500 transition-all',
  booked: 'bg-emerald-400 shadow hover:shadow-md hover:bg-emerald-500 transition-all',
  pending: 'bg-amber-300 shadow hover:shadow-md hover:bg-amber-400 transition-all'
};

// Status dot colors for the mobile view
const statusDotColors = {
  paid: 'bg-blue-400',
  booked: 'bg-emerald-400',
  pending: 'bg-amber-300'
};

// Helper function to safely parse date strings
const safeParseISO = (dateStr: string, timeStr: string): Date | null => {
  try {
    // Check if the date string already contains a time component (T)
    const cleanDateStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const dateTimeStr = `${cleanDateStr}T${timeStr}`;
    
    const parsedDate = parseISO(dateTimeStr);
    if (!isValid(parsedDate)) {
      console.warn(`Invalid date after parsing: ${dateTimeStr}`);
      return null;
    }
    return parsedDate;
  } catch (error) {
    console.error(`Error parsing date: ${dateStr} with time ${timeStr}`, error);
    return null;
  }
};

// Helper function to safely format dates
const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = 'Invalid date'): string => {
  if (!date || !isValid(date)) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error);
    return fallback;
  }
};

export function MonthlyCalendar({
  rooms,
  bookings,
  selectedRoom,
  date,
  onDayClick,
  onMonthChange,
  onBookingUpdate,
  onBookingDelete
}: MonthlyCalendarProps) {
  const { isMobile, isTablet } = useDevice();
  const isCompactView = isMobile || isTablet;
  const [selectedBooking, setSelectedBooking] = useState<{
    booking: Booking;
    room: Room;
  } | null>(null);
  
  // For mobile, we might want to show selected day details
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Calculate empty cells before the first day of the month
  // Convert Sunday (0) to 6 and others to n-1 to make Monday (1) the first day
  const firstDayOfMonth = getDay(monthStart);
  const emptyDays = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const emptyCells = Array(emptyDays).fill(null);

  // Calculate total cells needed (empty + days + padding)
  const totalDays = emptyCells.length + days.length;
  const remainingCells = Math.ceil(totalDays / 7) * 7 - totalDays;
  const paddingCells = Array(remainingCells).fill(null);

  // Check for and log any invalid booking dates
  React.useEffect(() => {
    const invalidDates = bookings.filter(booking => {
      try {
        const date = safeParseISO(booking.date, booking.timeSlot);
        return !date;
      } catch (error) {
        return true;
      }
    });
    
    if (invalidDates.length > 0) {
      console.warn(`Found ${invalidDates.length} bookings with invalid dates in MonthlyCalendar`);
      invalidDates.forEach(b => {
        console.warn(`Invalid booking: id=${b.id}, date=${b.date}, timeSlot=${b.timeSlot}`);
      });
    }
  }, [bookings]);

  const getBookingsForDay = (day: Date) => {
    try {
      const dateStr = format(day, 'yyyy-MM-dd');
      
      return bookings.filter(booking => {
        try {
          // Extract just the date part from potentially full ISO strings
          const bookingDateClean = booking.date.includes('T') 
            ? booking.date.split('T')[0] 
            : booking.date;
            
          return bookingDateClean === dateStr;
        } catch (error) {
          console.error(`Error comparing dates for booking: ${booking.id}`, error);
          return false;
        }
      }).sort((a, b) => {
        // Sort by status priority first, then time
        const statusPriority = { paid: 0, booked: 1, pending: 2 };
        const statusDiff = statusPriority[a.status] - statusPriority[b.status];
        if (statusDiff !== 0) return statusDiff;
        
        try {
          const aTime = parseInt(a.timeSlot.split(':')[0]);
          const bTime = parseInt(b.timeSlot.split(':')[0]);
          if (isNaN(aTime) || isNaN(bTime)) return 0;
          return aTime - bTime;
        } catch (error) {
          return 0;
        }
      });
    } catch (error) {
      console.error(`Error in getBookingsForDay for ${format(day, 'yyyy-MM-dd')}:`, error);
      return [];
    }
  };

  // Get booking status counts for a specific day
  const getBookingStatusCounts = (day: Date) => {
    const dayBookings = getBookingsForDay(day);
    const counts = {
      paid: 0,
      booked: 0,
      pending: 0
    };
    
    dayBookings.forEach(booking => {
      counts[booking.status]++;
    });
    
    return counts;
  };

  const handleBookingClick = (e: React.MouseEvent, booking: Booking, room: Room) => {
    e.stopPropagation();
    setSelectedBooking({ booking, room });
  };

  const getRoomById = (roomId: number) => {
    return rooms.find(room => room.id === roomId);
  };

  const handleMonthChange = (monthName: string) => {
    const monthIndex = months.indexOf(monthName);
    onMonthChange(monthIndex);
  };

  const handlePrevMonth = () => {
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    onMonthChange(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    onMonthChange(nextMonth);
  };

  const handleKeyPress = useCallback((e: React.KeyboardEvent, day: Date) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onDayClick?.(day);
    }
  }, [onDayClick]);

  const handleBookingKeyPress = useCallback((
    e: React.KeyboardEvent,
    booking: Booking,
    room: Room
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setSelectedBooking({ booking, room });
    }
  }, []);

  const handleDuplicateComplete = (date: Date) => {
    onDayClick?.(date);
  };

  const handleDaySelect = (day: Date) => {
    if (isCompactView) {
      setSelectedDate(day);
    } else {
      onDayClick?.(day);
    }
  };

  const navigationButtonClass = "h-10 w-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all p-0 flex items-center justify-center";
  const navigationIconClass = "h-4 w-4 text-black";
  
  // Render mobile day details view
  const renderDayDetails = (day: Date) => {
    const bookingsForDay = getBookingsForDay(day);
    
    return (
      <div className="bg-white p-4 rounded-lg border mt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            {format(day, 'EEEE, MMMM d')}
          </h3>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSelectedDate(null)}
            className="text-gray-500"
          >
            Back to Calendar
          </Button>
        </div>
        
        {bookingsForDay.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No bookings for this day
          </div>
        ) : (
          <div className="space-y-3">
            {bookingsForDay.map(booking => {
              const room = getRoomById(booking.roomId);
              if (!room) return null;
              
              // Validate date before rendering
              try {
                return (
                  <div
                    key={booking.id}
                    className={cn(
                      "p-3 rounded-md",
                      statusColors[booking.status]
                    )}
                    onClick={(e) => handleBookingClick(e, booking, room)}
                  >
                    <div className="flex justify-between">
                      <div>
                        <div className="font-medium">{booking.clientName}</div>
                        <div className="text-sm">{room.name}</div>
                        <div className="text-xs mt-1">
                          {booking.timeSlot} • {booking.duration}h
                        </div>
                      </div>
                      <div className="text-xs">
                        {booking.bookedBy}
                      </div>
                    </div>
                  </div>
                );
              } catch (error) {
                console.error(`Error rendering booking in day details: ${booking.id}`, error);
                return null;
              }
            })}
          </div>
        )}
      </div>
    );
  };
  
  // Mobile view
  const renderMobileView = () => {
    if (selectedDate) {
      return renderDayDetails(selectedDate);
    }
    
    return (
      <div className="p-2">
        {/* Month navigation controls are removed from here as they're already in ViewSelector */}
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Day Headers */}
          <div 
            className="grid grid-cols-7 bg-gray-50 border-b"
            style={{ gridTemplateColumns: 'repeat(7, minmax(32px, 1fr))' }}
            role="row"
          >
            {shortWeekDays.map(day => (
              <div 
                key={day} 
                className="text-center py-2 font-semibold text-gray-600 text-xs"
                role="columnheader"
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div 
            className="grid grid-cols-7"
            style={{ 
              gridTemplateColumns: 'repeat(7, minmax(32px, 1fr))',
              gridTemplateRows: `repeat(${Math.ceil((totalDays + remainingCells) / 7)}, 60px)`
            }}
          >
            {/* Empty cells for days before the first of the month */}
            {emptyCells.map((_, index) => (
              <div
                key={`empty-${index}`}
                className="border-b border-r p-1 bg-gray-50/30"
              />
            ))}
            
            {/* Actual days */}
            {days.map(day => {
              const dayBookings = getBookingsForDay(day);
              const statusCounts = getBookingStatusCounts(day);
              const isCurrentDay = isToday(day);
              const bookingCount = dayBookings.length;
              
              return (
                <div
                  key={day.toString()}
                  className={cn(
                    "border-b border-r p-1 transition-colors",
                    isCurrentDay && "ring-2 ring-inset ring-blue-500",
                    bookingCount > 0 && "bg-blue-50/30"
                  )}
                  onClick={() => handleDaySelect(day)}
                >
                  <div className="flex flex-col h-full">
                    <div className="mb-0.5">
                      <span className={cn(
                        "inline-flex items-center justify-center w-6 h-6 rounded-full text-xs",
                        isCurrentDay ? "bg-blue-500 text-white font-medium" : "text-gray-700"
                      )}>
                        {format(day, 'd')}
                      </span>
                    </div>
                    
                    {bookingCount > 0 && (
                      <div className="flex flex-col gap-1 items-center">
                        {/* Status indicators - show colored dots for each status with count */}
                        <div className="flex flex-wrap justify-center gap-1">
                          {statusCounts.paid > 0 && (
                            <div className="flex items-center">
                              <span className={cn("w-2 h-2 rounded-full", statusDotColors.paid)}></span>
                              <span className="text-[10px] ml-0.5 text-blue-700">{statusCounts.paid}</span>
                            </div>
                          )}
                          {statusCounts.booked > 0 && (
                            <div className="flex items-center">
                              <span className={cn("w-2 h-2 rounded-full", statusDotColors.booked)}></span>
                              <span className="text-[10px] ml-0.5 text-emerald-700">{statusCounts.booked}</span>
                            </div>
                          )}
                          {statusCounts.pending > 0 && (
                            <div className="flex items-center">
                              <span className={cn("w-2 h-2 rounded-full", statusDotColors.pending)}></span>
                              <span className="text-[10px] ml-0.5 text-amber-700">{statusCounts.pending}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Padding cells for the end of the month */}
            {paddingCells.map((_, index) => (
              <div
                key={`padding-${index}`}
                className="border-b border-r p-1 bg-gray-50/30"
              />
            ))}
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex gap-4 text-xs mt-4 justify-center">
          <div className="flex items-center gap-1">
            <div className={cn("w-3 h-3 rounded-full", statusDotColors.paid)} />
            <span className="text-gray-700">Paid</span>
          </div>
          <div className="flex items-center gap-1">
            <div className={cn("w-3 h-3 rounded-full", statusDotColors.booked)} />
            <span className="text-gray-700">Booked</span>
          </div>
          <div className="flex items-center gap-1">
            <div className={cn("w-3 h-3 rounded-full", statusDotColors.pending)} />
            <span className="text-gray-700">Pending</span>
          </div>
        </div>
      </div>
    );
  };

  // Desktop view
  const renderDesktopView = () => {
    return (
      <div className="p-6 space-y-8 w-full">
        <div className="max-w-[1400px] mx-auto space-y-8">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-48">
                <Label htmlFor="month-select" className="text-sm font-medium mb-1.5 block">
                  Select Month
                </Label>
                <Select
                  value={months[currentMonth]}
                  onValueChange={handleMonthChange}
                >
                  <SelectTrigger id="month-select" className="w-full">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(month => (
                      <SelectItem key={month} value={month}>
                        {month} {currentYear}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6" role="group" aria-label="Month navigation">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePrevMonth}
                  className={navigationButtonClass}
                  aria-label="Previous month"
                >
                  <ChevronLeft className={navigationIconClass} aria-hidden="true" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextMonth}
                  className={navigationButtonClass}
                  aria-label="Next month"
                >
                  <ChevronRight className={navigationIconClass} aria-hidden="true" />
                </Button>
              </div>
            </div>
            <div className="text-2xl font-semibold pt-6 text-gray-900" aria-live="polite">
              {format(date, 'MMMM yyyy')}
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Day Headers */}
            <div 
              className="grid grid-cols-7 bg-gray-50 border-b"
              style={{ 
                gridTemplateColumns: 'repeat(7, minmax(180px, 1fr))'
              }}
              role="row"
            >
              {weekDays.map(day => (
                <div 
                  key={day} 
                  className="text-center py-3 font-semibold text-gray-600 text-sm"
                  role="columnheader"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div 
              className="grid grid-cols-7"
              style={{ 
                gridTemplateColumns: 'repeat(7, minmax(180px, 1fr))',
                minHeight: '700px',
                gridTemplateRows: `repeat(${Math.ceil((totalDays + remainingCells) / 7)}, minmax(120px, 1fr))`
              }}
              role="grid"
            >
              {/* Empty cells for days before the first of the month */}
              {emptyCells.map((_, index) => (
                <div
                  key={`empty-${index}`}
                  className="border-b border-r p-2 bg-gray-50/30"
                  role="gridcell"
                />
              ))}

              {/* Actual days */}
              {days.map(day => {
                const dayBookings = getBookingsForDay(day);
                const isCurrentDay = isToday(day);

                return (
                  <div
                    key={day.toString()}
                    className={cn(
                      "border-b border-r p-2 transition-colors hover:bg-gray-50/50",
                      isCurrentDay && "ring-2 ring-inset ring-blue-500"
                    )}
                    onClick={() => onDayClick?.(day)}
                    onKeyPress={(e) => handleKeyPress(e, day)}
                    role="gridcell"
                    tabIndex={0}
                    aria-label={safeFormatDate(day, 'EEEE, MMMM d, yyyy')}
                    aria-current={isCurrentDay ? 'date' : undefined}
                  >
                    {/* Day Number */}
                    <div className="mb-2">
                      <span className={cn(
                        "inline-flex items-center justify-center w-7 h-7 rounded-full text-sm",
                        isCurrentDay ? "bg-blue-500 text-white font-medium" : "text-gray-700 font-medium"
                      )}>
                        {format(day, 'd')}
                      </span>
                    </div>

                    {/* Bookings */}
                    <div className="space-y-1 max-h-[120px] overflow-y-auto">
                      {dayBookings.map(booking => {
                        try {
                          const room = getRoomById(booking.roomId);
                          if (!room) return null;

                          // Ensure valid date before rendering
                          const bookingDate = safeParseISO(booking.date, booking.timeSlot);
                          if (!bookingDate) {
                            console.warn(`Invalid booking date: ${booking.id}, date: ${booking.date}, time: ${booking.timeSlot}`);
                            return null;
                          }

                          return (
                            <div
                              key={booking.id}
                              className={cn(
                                "px-2 py-1.5 rounded-md text-white text-xs cursor-pointer",
                                statusColors[booking.status]
                              )}
                              onClick={(e) => handleBookingClick(e, booking, room)}
                              onKeyPress={(e) => handleBookingKeyPress(e, booking, room)}
                              role="button"
                              tabIndex={0}
                              aria-label={`${booking.clientName} booking in ${room.name} at ${booking.timeSlot}`}
                            >
                              <div className="font-medium truncate">
                                {!selectedRoom && `${room.name} - `}{booking.clientName}
                              </div>
                              <div className="text-[10px] opacity-90">
                                {booking.timeSlot} ({booking.duration}h)
                              </div>
                            </div>
                          );
                        } catch (error) {
                          console.error(`Error rendering booking in monthly view: ${booking.id}`, error);
                          return null;
                        }
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Padding cells for the end of the month */}
              {paddingCells.map((_, index) => (
                <div
                  key={`padding-${index}`}
                  className="border-b border-r p-2 bg-gray-50/30"
                  role="gridcell"
                />
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-400" />
              <span className="text-gray-700">Paid</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-400" />
              <span className="text-gray-700">Booked</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-300" />
              <span className="text-gray-700">Pending</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isCompactView ? renderMobileView() : renderDesktopView()}

      {selectedBooking && (
        <EditBookingDialog
          booking={selectedBooking.booking}
          room={selectedBooking.room}
          onClose={() => setSelectedBooking(null)}
          onUpdate={onBookingUpdate}
          onDelete={onBookingDelete}
          onDuplicate={handleDuplicateComplete}
        />
      )}
    </>
  );
}